import url from "url"
import omitBy from "lodash/omitBy"
import isNil from "lodash/isNil"
import RequestError from "src/classes/RequestError"
import {
  selectToken,
  selectTokenType,
} from "src/engine/modules/session/selectors"
import env from "src/env"
import bridge from "../bridge"

const { REACT_APP_API_URL, REACT_APP_APPLICATIONS_REST_API } = env

export const joinPathname = (...paths) =>
  paths
    .map(p => p.replace(/^\/+|\/+$/g, ``)) // trim slashes
    .join(`/`)

export const isAbsoluteUrl = urlString => /^[a-z][a-z0-9+-]*:/.test(urlString)

export const buildUrl = (apiHostname, pathname, query) => {
  let hostname = apiHostname

  /* Url with a host, but without a protocol */
  if (hostname.startsWith(`//`)) {
    hostname = `${window.location.protocol}${hostname}`
  }

  if (isAbsoluteUrl(pathname)) {
    const parsed = url.parse(pathname, false, true)
    return url.format({
      ...parsed,
      query,
    })
  }

  if (!isAbsoluteUrl(hostname)) {
    /* Without host, but with a relative path */
    if (hostname.startsWith(`/`)) {
      hostname = `${window.location.protocol}//${window.location.host}${hostname}`
    } else {
      hostname = `${window.location.protocol}//${hostname}`
    }
  }

  const {
    hostname: originHostname,
    pathname: originPathname,
    port,
    protocol,
  } = url.parse(hostname, false, true)

  const result = url.format({
    protocol,
    hostname: originHostname,
    port,
    pathname: joinPathname(originPathname || ``, pathname),
    query,
  })

  return result
}

export default function request(pathname, query, options = {}) {
  const hostname = REACT_APP_API_URL || window.location.host

  const { store } = bridge

  const state = store.getState()
  const tokenType = selectTokenType(state)
  const accessToken = selectToken(state)

  // TODO: Kostil! Solve that account can not handle Authorization header. Forced by Sasha Voronin.
  if (pathname === REACT_APP_APPLICATIONS_REST_API) {
    /* eslint no-param-reassign: 0 */
    query = {
      ...query,
      token: accessToken,
    }
  }

  const url = buildUrl(hostname, pathname, query)

  const headers = {
    /* Enhance headers with standard rest api configuration to work with json */
    Accept: `application/json`,
    "Content-Type": `application/json`,
    ...options.headers,
  }

  /* Autoextract user token from redux store */
  if (tokenType && accessToken) {
    headers.Authorization = `${tokenType} ${accessToken}`
  }

  /* Hack to prevent error when we do not need custom Content-Type */
  if (headers[`Content-Type`] === undefined) {
    delete headers[`Content-Type`]
  }

  /* Autoconvert body to json string */
  if (
    headers[`Content-Type`] === `application/json` &&
    options.body &&
    typeof options.body === `object`
  ) {
    options.body = JSON.stringify(options.body)
  }

  const modifiedOptions = {
    ...options,
    /* Prevent server Access-Control-Allow-Headers error for empty headers */
    headers: omitBy(headers, isNil),
  }

  /* Define request object to debug reports */
  const request = {
    url,
    options: modifiedOptions,
    timestamp: new Date(),
  }

  // TODO: Kostil! Solve that account can not handle Authorization header. Forced by Sasha Voronin.
  if (pathname === REACT_APP_APPLICATIONS_REST_API) {
    delete request.options.headers.Authorization
  }

  return fetch(request.url, request.options)
    .then(response => {
      switch (response.status) {
        case 204:
          return Promise.resolve({
            data: {},
            response,
          })
        case 200:
          return response
            .json()
            .then(data => ({
              data,
              response,
            }))
            .catch(error => {
              throw new RequestError(error, request, response, null)
            })
        default: {
          return response.json
            ? response
                .json()
                .then(data =>
                  Promise.reject(
                    new RequestError(
                      new Error(`Request failed`),
                      request,
                      response,
                      data,
                    ),
                  ),
                )
            : Promise.reject(
                new RequestError(
                  new Error(`Response has no json API`),
                  request,
                  response,
                  null,
                ),
              )
        }
      }
    })
    .then(({ data, response }) => {
      if (!response.ok) {
        return Promise.reject(
          new RequestError(new Error(response.status), request, response),
        )
      }

      return Promise.resolve({ data, response })
    })
}
